import * as React from "react";
import { MoreVertical16Regular, Settings20Regular } from "@fluentui/react-icons";
import { ILookupDictionary } from "../../../../lookup/types/ILookupDictionary";
import { hideModal, showUpdateFieldsModal, showYesNoPrompt } from "../../../../redux/reducers/ModalReducer";
import { addMatchWorkToUsage, addTab, clearModalSearchResults, setChangesMade, nextWorkflowInSession, setEditableFields } from "../../../../redux/reducers/RepertoireReducer";
import { IAdjustmentDetail, IAdjustmentDetails } from "../../../../redux/types/IAdjustmentDetails";
import { IAdjustmentState, IAdjustmentStateKeys } from "../../../../redux/types/IAdjustmentState";
import IRepertoireComponentDataItem from "../../../../redux/types/IRepertoireComponentDataItem";
import { ITabReduxItem } from "../../../../redux/types/ITabReduxItem";
import ActionButton from "../../../components/actionButton/ActionButton";
import { SizedTextDataInput } from "../../../components/textDataInput/TextDataInput";
import {
    ADJUSTMENT_SEARCH_IP_NUMBERS,
    ADJUSTMENT_SEARCH_PRODUCT_NUMBERS,
    ADJUSTMENT_SEARCH_WORK_NUMBERS,
    ADJUSTMENT_SELECT_ALLOCATIONS,
    BLANK_MATCH_SEGMENT,
    ADJUSTMENT_ALLOCATION_USAGE_DETAIL_FIELDS,
    ADJUSTMENT_MAINTENANCE_GRIDS_VIEW,
    ALL_ROLES,
    CONFIGURE_ADJUSTMENTS_ROLE,
    EDITABLE_FIELDS,
    TITLE_EDITABLE_FIELDS_VIEW,
    ADJUSTMENT_SEARCH_USAGE_GROUP_TITLE
} from "../../../Consts";
import { IRepertoireField } from "../../../types/IRepertoireField";
import { IDistribution } from "../../../types/usageTypes/IDistribution";
import { IDistributionPool } from "../../../types/usageTypes/IDistributionPool";
import AdjustmentMaintenanceToolbar from "../adjustmentMaintenanceToolbar/AdjustmentMaintenanceToolbar";
import AllocationAdjustmentDetailsView from "../allocationAdjustmentDetailsView/AllocationAdjustmentDetailsView";
import AllocationSelectionView from "../allocationSelectionView/AllocationSelectionView";
import { AllocationDetailRowType, AllocationHeaderRowType } from "../../../types/usageTypes/IAllocationRowTypes";
import { IDataActionToolbar } from "../../../types/IDataActionToolbar";
import { FormatFields } from "../../../../redux/types/FormatFields";
import { If } from "../../../../core/components/If";
import { IAllocationSearchModel } from "../../../../redux/types/IAllocationSearchModel";
import { IUsagesSearchQuery } from "../../../types/usageTypes/IUsagesSearchQuery";
import { IWorksSearchQuery } from "../../../types/IWorksSearchQuery";
import { IWorksSearchResult } from "../../../types/IWorksSearchResult";
import { ITreeData } from "../../../types/ITreeData";
import { IMatchWorksSearchQuery } from "../../../types/usageTypes/IMatchWorksSearchQuery";
import { IMatchWorksSearchResult } from "../../../types/usageTypes/IMatchWorksSearchResult";
import { IDistributionType } from "../../../types/usageTypes/IDistibutionType";
import { IAdjustmentMaintenanceStateKeys } from "../../../../redux/types/IAdjustmentMaintenanceState";
import { IWorkflowParams } from "../../../types/IWorkFlowParams";
import { IWorkflowSearchResult } from "../../../types/IWorkflowSearchResult";
import AdjustmentMaintenanceGeneralDataView from "../adjustmentMaintenanceGeneralDataView/AdjustmentMaintenanceGeneralDataView";
import { DEFAULT_ALLOCATION_SEARCH_PAGE_SIZE } from "../../../../redux/constants/AllocationSearchConstants";
import IEditableFieldsComponentData from "../../../types/IEditableFieldsComponentData";
import { IShowFieldSettingViewModalProps } from "../../../components/repertoireModal/RepertoireModal";
import { useEffect } from "react";
import { buildAllocationSearchModel } from "../../utils/allocationSearchUtils";
import { SocietyAffiliationRightType } from "../../../types/ISocietyAffiliation";
import {
    getUsageDetailsForCheckboxSelection,
    isMatchingAdjustmentDetailFromRow,
    recalculateForFromTotalChange,
    sortAdjustmentDetailsByAllocationOrder,
    applyAdjustmentHeaderPoolAndSegmentToAllDetails
} from "../../../services/AdjustmentShareHelpers";

const isValidSegmentOption = (segment?: string | null): segment is string =>
    segment != null && segment !== "" && segment !== BLANK_MATCH_SEGMENT;

const buildSegmentOptionsFromPools = (
    pools: IDistributionPool[],
    poolCode: string
): { code: string; description: string }[] =>
    [...new Set(
        pools
            .filter(p => p.poolCode === poolCode)
            .map(p => p.segment)
            .filter(isValidSegmentOption)
    )]
        .sort()
        .map(seg => ({ code: seg, description: seg }));

const buildSourceSegments = (
    distributions: IDistribution[],
    distributionCode?: string,
    selectedPools: string[] = []
): { code: string; description: string }[] => {
    if (!distributionCode || !selectedPools?.length) {
        return [];
    }

    const distribution = distributions.find(
        d => d.distributionCode === distributionCode
    );
    if (!distribution?.distributionPools?.length) {
        return [];
    }

    return buildSegmentOptionsFromPools(distribution.distributionPools, selectedPools[0]);
};

export interface IAdjustmentMaintenancePageProps {
    adjustmentMaintenanceGeneralDataViewData: IRepertoireComponentDataItem;
    adjustmentMaintenanceToolbarData: IRepertoireComponentDataItem;
    adjustmentMaintenanceGridsViewData: IRepertoireComponentDataItem;
    adjustmentMaintenanceSaveResultData?: IRepertoireComponentDataItem;
    allocationAdjustmentDetailsViewData: IRepertoireComponentDataItem;
    dataGridTableData: IRepertoireComponentDataItem;
    adjustment: IAdjustmentState;
    setChangesMade: typeof setChangesMade;
    updateAdjustmentField: (value: any, fieldName: IAdjustmentStateKeys) => void;
    updateAllocationRecordField: (value: any, fieldName: IAdjustmentMaintenanceStateKeys) => void;
    activeTab: number;
    tabs: ITabReduxItem[];
    showModal: (
        modalContent: string,
        modalComponentInstance: string,
        modalProps: any,
        displayModalCloseButton: boolean,
        title: string
    ) => void;
    showYesNoPrompt: typeof showYesNoPrompt;
    hideModal: typeof hideModal;
    addTab: typeof addTab;
    clearModalSearchResults: typeof clearModalSearchResults;
    saveChanges: (adjustment: IAdjustmentState,
        isWorkflowSession: boolean,
        workflowsSessionItems?: IWorkflowSearchResult[],
        currentWorkflowIndex?: number,
        activeTab?: number,
        saveOption?: string,
        isOpenForWorkflowSession?: boolean) => void;
    addNewAdjustment: (lookupValues: ILookupDictionary, isNew?: boolean, adjustmentMaintenanceGeneralViewData?: IRepertoireComponentDataItem) => void;
    updateComponentFieldsItems: (fields: IRepertoireField[],
        componentName: string,
        componentInstance: string,
        componentDataFieldName: string,
        tabs: ITabReduxItem[],
        activeTab: number) => void;
    editableFields?: IEditableFieldsComponentData;
    editableFieldsDataView: IRepertoireComponentDataItem;
    setEditableFields: typeof setEditableFields;
    showUpdateFieldsModal: typeof showUpdateFieldsModal;
    undoAdjustmentChanges: (adjustmentId: string) => void;
    resetMessageBanner: () => void;
    distributions: IDistribution[];
    copyAdjustment: () => void;
    deleteAdjustment: (adjustmentID: string, activeTab: number) => void;
    searchAllocation: (searchParams: IAllocationSearchModel) => Promise<void>;
    allocationRecords?: AllocationHeaderRowType[];
    postAdjustments: (batchId: number) => void;
    saveInProgress: boolean;
    adjustmentBatchOperationInProgress: boolean;
    searchAdjustmentVersionHistory: (adjustmentId: string, lookups: ILookupDictionary, actionList: IDataActionToolbar[], formats: FormatFields[]) => void;
    roles?: string[];
    searchUsages?: (query: IUsagesSearchQuery, modalOpen: boolean, usageType: string, lookupSources: ITreeData[], continuationToken?: string) => void;
    resetPagination?: (repertoireSection: string) => void;
    updatePagination?: (indexOfFirstResult: number, indexOfLastResult: number, resultsPerPage: number, currentPage: number, repertoireSection: string) => void;
    sortSearchResults?: (name: string, results: any) => void;
    searchWorks?: (query: IWorksSearchQuery, modalOpen: boolean) => void;
    worksSearchResults?: IWorksSearchResult[];
    expandedWorkResults?: number[];
    searchSuccessful?: boolean;
    expandAll?: boolean;
    indexOfFirstResult?: number;
    indexOfLastResult?: number;
    resultsPerPage?: number;
    expandWorkResult?: (workResult: number) => void;
    expandAllResults?: () => void;
    currentPage?: number;
    username?: string;
    searchMatchWorks?: (query: IMatchWorksSearchQuery, modalOpen: boolean) => void;
    matchWorkSearchResult?: IMatchWorksSearchResult[];
    expandedMatchWorkResults?: number[];
    distributionTypes?: IDistributionType[];
    lookupSources?: ITreeData[];
    usageMatchingDefaultsWorks?: string[];
    addMatchworkToUsage?: typeof addMatchWorkToUsage;
    usageExpandAllResults?: () => void;
    expandMatchWorkResult?: (matchWorkResults: number) => void;
    paneIndex?: number;
    formatFields?: FormatFields[];
    updateUsagesAdvanced?: boolean;
    lookupValues?: ILookupDictionary;
    searchResultsTableData?: IRepertoireComponentDataItem;
    searchViewData?: IRepertoireComponentDataItem;
    getWorkDetails?: (dataSource: string, workID: number, lookups: ILookupDictionary, otherIndicatorsWorkFlagTypes: string[], readonlyIndicatorsWorkFlagTypes: string[], dataActions?: IDataActionToolbar[], workMaintenanceGeneralDataViewData?: IRepertoireComponentDataItem, formats?: FormatFields[], openEntityForWorflowSession?: boolean, isOpenedInSecondTab?: boolean) => void;
    saveSuccessful?: boolean;
    dataSource: string;
    workflowFieldsData: IRepertoireComponentDataItem;
    isWorkflowSessionStarted?: boolean;
    cancelWorkflowSession?: (activeTab: number) => void;
    nextWorkflowItem?: typeof nextWorkflowInSession;
    saveWorkflow?: (workflowparams: IWorkflowParams) => void;
    isOpenForWorkflowSession?: boolean;
    skipWorkflowItem?: (
        activeTab: number
        , currentWorkflowIndex: number
        , workflows: IWorkflowSearchResult[]
        , lookups: ILookupDictionary
        , otherIndicatorsWorkFlagTypes: string[]
        , dataActions: IDataActionToolbar[]
        , workMaintenanceGeneralDataViewData: IRepertoireComponentDataItem
        , formats: FormatFields[]
        , readonlyIndicatorsWorkFlagTypes: string[]) => void;
    workflowSessionItems?: IWorkflowSearchResult[];
    currentWorkflowIndex?: number;
    dataActionList?: IDataActionToolbar[];
    completeWorkflowOnSave?: (value: boolean) => void;
    enabledAdjustmentICEWorkSearch: boolean;
    customer?: string;
    allocationSearchContinuationToken?: number | null;
    allocationSearchHasMore?: boolean;
    allocationSearchPageSize?: number;
}

interface IAdjustmentMaintenancePageState {
    scroll: number;
    toolbarWidth: number | null;
    sourcePools: { code: string; description: string; }[];
    sourceSegments: { code: string; description: string; }[];
    sourceDistributions: { code: string; description: string }[];
    adjustmentPools: { code: string; description: string; }[];
    adjustmentSegments: { code: string; description: string; }[];
    adjustmentDistributions: { code: string; description: string }[];
    searching: boolean;
    workNumbersToSearch?: string[];
    ipNumbersToSearch?: string[];
    productNumbersToSearch?: string[];
    usageGroupTitleToSearch?: string;
    adjustmentState?: IAdjustmentState;
    updatedAllocationRecord?: AllocationHeaderRowType[];
    isAdjustWork?: boolean;
    useIpTemplateForCheckboxSelection?: boolean;
    isCompleteWorkflowOnSave: boolean;
}

const initialState: IAdjustmentMaintenancePageState = {
            scroll: 0,
            toolbarWidth: null,
            sourceDistributions: [],
            sourcePools: [],
            sourceSegments: [],
            adjustmentDistributions: [],
            adjustmentPools: [],
            adjustmentSegments: [],
            searching: false,
            workNumbersToSearch: [],
            ipNumbersToSearch: [],
            productNumbersToSearch: [],
            usageGroupTitleToSearch: "",
            updatedAllocationRecord: [],
            isAdjustWork: false,
            useIpTemplateForCheckboxSelection: false,
            isCompleteWorkflowOnSave: true,
        }

const normalizeDistributionCode = (value?: string): string =>
    (value ?? "").trim().toUpperCase();

const AdjustmentMaintenancePage: React.FC<IAdjustmentMaintenancePageProps> = ({
    distributions,
    adjustment,
    allocationRecords,
    adjustmentMaintenanceGridsViewData,
    saveChanges,
    tabs,
    activeTab,
    isWorkflowSessionStarted,
    workflowSessionItems,
    currentWorkflowIndex,
    isOpenForWorkflowSession,
    undoAdjustmentChanges,
    updateAllocationRecordField,
    searchAllocation,
    customer,
    adjustmentMaintenanceGeneralDataViewData,
    adjustmentMaintenanceToolbarData,
    adjustmentMaintenanceSaveResultData,
    allocationAdjustmentDetailsViewData,
    showModal,
    hideModal,
    showYesNoPrompt,
    lookupValues,
    addNewAdjustment,
    updateAdjustmentField,
    saveInProgress,
    adjustmentBatchOperationInProgress,
    postAdjustments,
    searchAdjustmentVersionHistory,
    copyAdjustment,
    deleteAdjustment,
    clearModalSearchResults,
    roles,
    dataSource,
    workflowFieldsData,
    cancelWorkflowSession,
    nextWorkflowItem,
    saveWorkflow,
    skipWorkflowItem,
    dataActionList,
    searchSuccessful,
    indexOfFirstResult,
    resultsPerPage,
    resetPagination,
    updatePagination,
    searchMatchWorks,
    matchWorkSearchResult,
    expandMatchWorkResult,
    expandedMatchWorkResults,
    addMatchworkToUsage,
    lookupSources,
    distributionTypes,
    usageMatchingDefaultsWorks,
    usageExpandAllResults,
    searchResultsTableData,
    indexOfLastResult,
    currentPage,
    searchViewData,
    sortSearchResults,
    worksSearchResults,
    expandedWorkResults,
    expandAll,
    paneIndex,
    getWorkDetails,
    saveSuccessful,
    enabledAdjustmentICEWorkSearch,
    allocationSearchContinuationToken,
    allocationSearchHasMore,
    allocationSearchPageSize,
    dataGridTableData,
    editableFields,
    editableFieldsDataView,
    setEditableFields,
    showUpdateFieldsModal,
    updateComponentFieldsItems,
    resetMessageBanner,
}) => {

    const [state, setState] = React.useState(initialState);
    const toolbarParentRef = React.useRef<HTMLDivElement>(null);
    const [viewData, setViewData] = React.useState({})
    const [openAllocationUsageDetailFieldsModal, setOpenAllocationUsageDetailFieldsModal] = React.useState(false);
    const prevAllocationRecordsRef = React.useRef<any>(null);
    const allocationRecordsIdentityRef = React.useRef<any>(null);
    const isReadonly: boolean = !!(adjustment && adjustment.dataSource === 'Posted') || !!tabs[activeTab]?.isReadonly;
    
    const getPoolsAndSegments = (value: string, fieldName: keyof IAdjustmentState) => {
        let sourcePoolsList: IDistributionPool[] = [];
        let sourcePools: { code: string, description: string }[] = []
        let adjustmentPoolsList: IDistributionPool[]
        let adjustmentPools: { code: string, description: string }[] = []
        switch (fieldName) {
            case 'sourceDistribution':
                let sourceDistribution = distributions.find(
                    d => normalizeDistributionCode(d.distributionCode) === normalizeDistributionCode(value)
                );
                sourcePoolsList = sourceDistribution ? sourceDistribution.distributionPools : [];
                sourcePools = [...new Set(sourcePoolsList.map(p => p.poolCode))].sort().map(poolCode => ({ code: poolCode, description: poolCode }));
                setState(prevState => ({ ...prevState, sourcePools }));
                break;
            case 'adjustmentDistribution':
                let adjustmentDistribution = distributions.find(
                    d => normalizeDistributionCode(d.distributionCode) === normalizeDistributionCode(value)
                );
                adjustmentPoolsList = adjustmentDistribution?.distributionPools ? adjustmentDistribution.distributionPools : [];
                adjustmentPools = [...new Set(adjustmentPoolsList.map(p => p.poolCode))].sort().map(poolCode => ({ code: poolCode, description: poolCode }));
                setState(prevState => ({ ...prevState, adjustmentPools }));
                break;
            case 'adjustmentPool': {
                const selectedAdjustmentDistribution = distributions.find(
                    d => d.distributionCode === adjustment.adjustmentDistribution
                );

                adjustmentPoolsList = selectedAdjustmentDistribution?.distributionPools ?? [];

                if (!adjustmentPoolsList.length) {
                    setState(prevState => ({ ...prevState, adjustmentSegments: [] }));
                    break;
                }

                if (adjustmentPoolsList.find(p => p.poolCode === value)) {
                    const adjustmentSegments = buildSegmentOptionsFromPools(adjustmentPoolsList, value);
                    setState(prevState => ({ ...prevState, adjustmentSegments }));
                }

                break;
            }
        }
    }

    useEffect(() => {

        const fields = adjustmentMaintenanceGridsViewData?.fields;
        let currentViewData: any = { fields: fields ?? [] };


        if (fields) {
            const visibleFields = fields.filter(field => !field.hidden);
            visibleFields.forEach(item => {
                currentViewData[item.name] = item.data;
            });
            setViewData(currentViewData);
            setState(prevState => ({ ...prevState, adjustmentState: adjustment }));
        }

        if (distributions && distributions.length > 0) {
            let distributionSourceList = [];
            let distributionAdjustmentsList = [];
            distributions.forEach(distribution => {
                if (distribution.distributionStatus === "Closed") {
                    distributionSourceList.push({ code: distribution.distributionCode, description: distribution.description });
                } else {
                    distributionAdjustmentsList.push({ code: distribution.distributionCode, description: distribution.description });
                }
            });

            const selectedSourceCode = normalizeDistributionCode(adjustment?.sourceDistribution);
            const selectedAdjustmentCode = normalizeDistributionCode(adjustment?.adjustmentDistribution);

            if (selectedSourceCode) {
                const selectedSourceDistribution = distributions.find(
                    distribution => normalizeDistributionCode(distribution.distributionCode) === selectedSourceCode
                );

                if (
                    selectedSourceDistribution &&
                    !distributionSourceList.some(
                        item => normalizeDistributionCode(item.code) === selectedSourceCode
                    )
                ) {
                    distributionSourceList.push({
                        code: selectedSourceDistribution.distributionCode,
                        description: selectedSourceDistribution.description
                    });
                }
            }

            if (selectedAdjustmentCode) {
                const selectedAdjustmentDistribution = distributions.find(
                    distribution => normalizeDistributionCode(distribution.distributionCode) === selectedAdjustmentCode
                );

                if (
                    selectedAdjustmentDistribution &&
                    !distributionAdjustmentsList.some(
                        item => normalizeDistributionCode(item.code) === selectedAdjustmentCode
                    )
                ) {
                    distributionAdjustmentsList.push({
                        code: selectedAdjustmentDistribution.distributionCode,
                        description: selectedAdjustmentDistribution.description
                    });
                }
            }

            setState(prevState => ({ ...prevState, sourceDistributions: distributionSourceList, adjustmentDistributions: distributionAdjustmentsList }));
        }
        if (adjustment && adjustment?.sourceDistribution) {
            getPoolsAndSegments(adjustment.sourceDistribution, 'sourceDistribution');
            if (adjustment.sourcePool && adjustment.sourcePool.length == 1)
                getPoolsAndSegments(adjustment.sourcePool[0], 'sourcePool');
        }
        if (adjustment && adjustment.adjustmentDistribution) {
            getPoolsAndSegments(adjustment.adjustmentDistribution, 'adjustmentDistribution');
            if (adjustment.adjustmentPool)
                getPoolsAndSegments(adjustment.adjustmentPool, 'adjustmentPool')
        }
        if (allocationRecords && allocationRecords !== allocationRecordsIdentityRef.current) {
            allocationRecordsIdentityRef.current = allocationRecords;
            setState(prevState => ({ ...prevState, updatedAllocationRecord: allocationRecords }));
        }

    }, [
        distributions,
        adjustment,
        allocationRecords,
        adjustmentMaintenanceGridsViewData
    ])

    useEffect(() => {
        if (
            adjustmentMaintenanceGridsViewData?.fields?.length &&
            (!editableFields || !editableFields.default || editableFields.default.length === 0)
        ) {
            setEditableFields(
                ADJUSTMENT_MAINTENANCE_GRIDS_VIEW,
                ADJUSTMENT_ALLOCATION_USAGE_DETAIL_FIELDS
            );
        }
    }, [adjustmentMaintenanceGridsViewData?.fields, editableFields, setEditableFields]);

    useEffect(() => {
        setState(prevState => ({
            ...prevState,
            useIpTemplateForCheckboxSelection: (adjustment?.adjustmentDetails?.length ?? 0) > 0
        }));
    }, [adjustment?.id]);

    useEffect(() => {
        const prevAllocationRecords = prevAllocationRecordsRef.current;

        if (state.searching && allocationRecords) {
            setState(prevState => ({ ...prevState, searching: false }));
        }
        if (
            allocationRecords &&
            allocationRecords.length > 0 &&
            prevAllocationRecords !== allocationRecords
        ) {
            let updatedRecords = [...allocationRecords];
            if (adjustment?.adjustmentDetails?.length > 0) {
                updatedRecords = updatedRecords.map(item2 => {
                    const match = adjustment.adjustmentDetails.find(
                        item1 => String(item2.AllocationUsageHeaderId) === String(item1.id)
                    );

                    return match
                        ? { ...item2, IsSelected: true }
                        : item2;
                });
            }

            const needUpdate = updatedRecords.some((r, i) => r.IsSelected !== allocationRecords[i].IsSelected);
            if (needUpdate) {
                setState(prevState => ({
                    ...prevState,
                    updatedAllocationRecord: updatedRecords,
                    isAdjustWork: updatedRecords.some(
                        record => record.IsSelected
                    )
                }));
            }
        }
        prevAllocationRecordsRef.current = allocationRecords;
    }, [
        allocationRecords,
        adjustment,
        state.searching,
    ])

    const saveAdjustment = (saveOption?: string) => {
        if (state.isCompleteWorkflowOnSave) {
            if (adjustment.workflows && adjustment.workflows[0] && adjustment.workflows[0].status) {
                adjustment.workflows[0].status = "Completed";
            }
        }
        saveChanges(adjustment, isWorkflowSessionStarted, workflowSessionItems, currentWorkflowIndex + 1, activeTab, saveOption, isOpenForWorkflowSession);

    }

    const undoChanges = () => {
        undoAdjustmentChanges(adjustment.id);
    }

    const markAllocationHeaderSelected = (allocationHeaderId: number | string) => {
        const updatedArr = state.updatedAllocationRecord.map(item =>
            String(item.AllocationUsageHeaderId) === String(allocationHeaderId)
                ? { ...item, IsSelected: true }
                : item
        );

        setState(prevState => ({
            ...prevState,
            updatedAllocationRecord: updatedArr,
            isAdjustWork: updatedArr.some(item => item.IsSelected === true)
        }));
        updateAllocationRecordField(updatedArr, 'allocationHeaderDetails');
    };

    const updateAdjustmentDetailsInAllocationOrder = (details: IAdjustmentDetails[]) => {
        updateAdjustmentField(
            sortAdjustmentDetailsByAllocationOrder(
                details.map(recalculateForFromTotalChange),
                state.updatedAllocationRecord
            ),
            'adjustmentDetails'
        );
    };

    const addWorkAdjustmentDetails = (selectedAllocationRecord: AllocationHeaderRowType): boolean => {
        let adjustmentDetail: IAdjustmentDetails;
        const hadNoDetails = !adjustment.adjustmentDetails?.length;

        const existingDetailForThisAllocation = adjustment.adjustmentDetails?.find(
            detail => String(detail.id) === String(selectedAllocationRecord.AllocationUsageHeaderId)
        );

        const shouldFilterByTemplate = state.useIpTemplateForCheckboxSelection && !hadNoDetails;
        const usageDetailsForFromRows = getUsageDetailsForCheckboxSelection(
            selectedAllocationRecord.UsageDetails ?? [],
            adjustment.adjustmentDetails,
            shouldFilterByTemplate
        );

        if (shouldFilterByTemplate && usageDetailsForFromRows.length === 0) {
            return false;
        }

        const usageGroupTitle =
            selectedAllocationRecord.UsageGroupTitle
            || usageDetailsForFromRows.find(item => item.UsageGroupTitle)?.UsageGroupTitle
            || '';

        const adjustmentDetailFrom = usageDetailsForFromRows.map((item, index) => ({
            adjustmentDetailID: index,
            adjustmentId: adjustment.id,
            allocationHeaderID: selectedAllocationRecord.AllocationUsageHeaderId,
            distribution: adjustment.sourceDistribution,
            pool: selectedAllocationRecord.PoolCode,
            segment: adjustment.sourceSegment,
            workNumber: selectedAllocationRecord.WorkNumber,
            workName: selectedAllocationRecord.WorkName,
            usageGroupTitle: item.UsageGroupTitle || usageGroupTitle,
            accountNumber: item.AccountNumber,
            name: item.PayeeName,
            role: item.Role,
            ipNumber: item.PayeeIpNameNumber,
            societyCode: item.SocietyCode,
            sharePercentAdjusted: item.SharePct,
            pointsAdjusted: item.PointsAllocated,
            amountAdjusted: item.Amount
        }));

        const adjustmentDetailTo: IAdjustmentDetail = {
            adjustmentDetailID: 0,
            adjustmentId: adjustment.id,
            allocationHeaderID: selectedAllocationRecord.AllocationUsageHeaderId,
            distribution: adjustment.adjustmentDistribution,
            pool: (adjustment.adjustmentPool !== '' && adjustment.adjustmentPool != undefined) ? adjustment.adjustmentPool : selectedAllocationRecord.PoolCode,
            segment: adjustment.adjustmentSegment,
            workNumber: selectedAllocationRecord.WorkNumber,
            workName: selectedAllocationRecord.WorkName,
            usageGroupTitle,
            accountNumber: '',
            name: '',
            role: '',
            ipNumber: undefined,
            sharePercentAdjusted: '',
            pointsAdjusted: '',
            amountAdjusted: ''
        }

        if (adjustment.adjustmentDetails && adjustment.adjustmentDetails.length > 0) {
            if (existingDetailForThisAllocation) {
                adjustmentDetail = existingDetailForThisAllocation;
                const newFromRows = adjustmentDetailFrom.filter(newRow =>
                    !adjustmentDetail.adjustmentDetailFrom.some(existing =>
                        isMatchingAdjustmentDetailFromRow(existing, newRow)
                    )
                );

                if (newFromRows.length > 0) {
                    adjustment.adjustmentDetails = adjustment.adjustmentDetails.filter(
                        x => String(x.id) !== String(adjustmentDetail.id)
                    );
                    newFromRows.forEach(row => {
                        row.adjustmentDetailID = adjustmentDetail.adjustmentDetailFrom.length;
                        adjustmentDetail.adjustmentDetailFrom.push(row);
                    });
                    adjustment.adjustmentDetails.push(adjustmentDetail);
                }
            } else {
                adjustmentDetail = {
                    id: selectedAllocationRecord.AllocationUsageHeaderId,
                    adjustmentDetailFrom,
                    adjustmentDetailTo: [adjustmentDetailTo]
                };
                adjustment.adjustmentDetails.push(adjustmentDetail);
            }
        } else {
            adjustmentDetail = {
                id: selectedAllocationRecord.AllocationUsageHeaderId,
                adjustmentDetailFrom,
                adjustmentDetailTo: [adjustmentDetailTo]
            };
            adjustment.adjustmentDetails = [adjustmentDetail];
        }

        updateAdjustmentDetailsInAllocationOrder(adjustment.adjustmentDetails);

        if (hadNoDetails) {
            setState(prevState => ({
                ...prevState,
                useIpTemplateForCheckboxSelection: false
            }));
        }

        return true;
    }

    const selectAllAllocationHeaders = (selectAll: boolean) => {
        if (!selectAll) {
            adjustment.adjustmentDetails = [];
            updateAdjustmentField(adjustment.adjustmentDetails, 'adjustmentDetails');
            const clearedArr = state.updatedAllocationRecord.map(item => ({ ...item, IsSelected: false }));
            setState(prevState => ({
                ...prevState,
                updatedAllocationRecord: clearedArr,
                isAdjustWork: false,
                useIpTemplateForCheckboxSelection: false
            }));
            updateAllocationRecordField(clearedArr, 'allocationHeaderDetails');
            return;
        }

        const updatedArr = state.updatedAllocationRecord.map(item => {
            const alreadyHasDetail = adjustment.adjustmentDetails?.some(
                detail => String(detail.id) === String(item.AllocationUsageHeaderId)
            );

            if (alreadyHasDetail) {
                return { ...item, IsSelected: true };
            }

            const added = addWorkAdjustmentDetails(item);
            return { ...item, IsSelected: added };
        });

        setState(prevState => ({
            ...prevState,
            updatedAllocationRecord: updatedArr,
            isAdjustWork: updatedArr.some(item => item.IsSelected === true)
        }));
        updateAllocationRecordField(updatedArr, 'allocationHeaderDetails');
    }

    const selectAllocationHeader = (selectedAllocationDetails: AllocationHeaderRowType) => {
        const headerID = selectedAllocationDetails.AllocationUsageHeaderId;
        const isCurrentlySelected = selectedAllocationDetails.IsSelected;

        if (!isCurrentlySelected) {
            const added = addWorkAdjustmentDetails(selectedAllocationDetails);
            if (!added) {
                return;
            }
        } else {
            if (adjustment.adjustmentDetails && adjustment.adjustmentDetails.length === 1) {
                adjustment.adjustmentDetails = [];
            } else {
                const details = adjustment.adjustmentDetails ?? [];
                adjustment.adjustmentDetails = details.filter(item => String(item.id) !== String(headerID));
            }
            updateAdjustmentField(adjustment.adjustmentDetails, 'adjustmentDetails');
        }

        const updatedArr = state.updatedAllocationRecord.map(item =>
            item.AllocationUsageHeaderId === headerID ? { ...item, IsSelected: !isCurrentlySelected } : item
        );
        setState(prevState => ({
            ...prevState,
            updatedAllocationRecord: updatedArr,
            isAdjustWork: updatedArr.some(item => item.IsSelected === true)
        }));
        updateAllocationRecordField(updatedArr, 'allocationHeaderDetails');
    }

    const addIPAdjustmentDetails = (selectedAllocationDetails: AllocationDetailRowType) => {
        let adjustmentDetail: IAdjustmentDetails;
        let detailChanged = false;

        const adjustmentDetailFrom: IAdjustmentDetail = {
            adjustmentDetailID: 0,
            adjustmentId: adjustment.id,
            allocationHeaderID: selectedAllocationDetails.AllocationUsageHeaderID,
            distribution: adjustment?.sourceDistribution,
            pool: selectedAllocationDetails.PoolCode,
            segment: adjustment.sourceSegment,
            workNumber: selectedAllocationDetails.WorkNumber,
            workName: selectedAllocationDetails.WorkName,
            usageGroupTitle: selectedAllocationDetails.UsageGroupTitle,
            accountNumber: selectedAllocationDetails.PayeeAccountNumber,
            name: selectedAllocationDetails.PayeeName,
            role: selectedAllocationDetails.Role,
            ipNumber: selectedAllocationDetails.PayeeIpNameNumber,
            societyCode: selectedAllocationDetails.SocietyCode,
            sharePercentAdjusted: selectedAllocationDetails.SharePct,
            pointsAdjusted: selectedAllocationDetails.PointsAllocated,
            amountAdjusted: selectedAllocationDetails.Amount
        };

        const adjustmentDetailTo: IAdjustmentDetail = {
            adjustmentDetailID: 0,
            adjustmentId: adjustment.id,
            allocationHeaderID: selectedAllocationDetails.AllocationUsageHeaderID,
            distribution: adjustment.adjustmentDistribution,
            pool: (adjustment.adjustmentPool !== '' && adjustment.adjustmentPool != undefined) ? adjustment.adjustmentPool : selectedAllocationDetails.PoolCode,
            segment: adjustment.adjustmentSegment,
            workNumber: selectedAllocationDetails.WorkNumber,
            workName: selectedAllocationDetails.WorkName,
            usageGroupTitle: selectedAllocationDetails.UsageGroupTitle,
            accountNumber: '',
            name: '',
            role: '',
            ipNumber: undefined,
            sharePercentAdjusted: '',
            pointsAdjusted: '',
            amountAdjusted: ''
        }

        if (adjustment.adjustmentDetails && adjustment.adjustmentDetails.length > 0) {

            adjustmentDetail = adjustment.adjustmentDetails.find(
                x => String(x.id) === String(selectedAllocationDetails.AllocationUsageHeaderID)
            );

            if (adjustmentDetail) {
                const alreadyExists = adjustmentDetail.adjustmentDetailFrom.some(existing =>
                    isMatchingAdjustmentDetailFromRow(existing, selectedAllocationDetails)
                );
                if (!alreadyExists) {
                    adjustment.adjustmentDetails = adjustment.adjustmentDetails.filter(
                        x => String(x.id) !== String(adjustmentDetail.id)
                    );
                    adjustmentDetailFrom.adjustmentDetailID = adjustmentDetail.adjustmentDetailFrom.length;
                    adjustmentDetail.adjustmentDetailFrom.push(adjustmentDetailFrom);
                    adjustment.adjustmentDetails.push(adjustmentDetail);
                    detailChanged = true;
                }
            }
            else {
                adjustmentDetail = {
                    id: selectedAllocationDetails.AllocationUsageHeaderID,
                    adjustmentDetailFrom: [adjustmentDetailFrom],
                    adjustmentDetailTo: [adjustmentDetailTo]
                }
                adjustment.adjustmentDetails.push(adjustmentDetail);
                detailChanged = true;
            }
        }
        else {
            adjustmentDetail = {
                id: selectedAllocationDetails.AllocationUsageHeaderID,
                adjustmentDetailFrom: [adjustmentDetailFrom],
                adjustmentDetailTo: [adjustmentDetailTo]
            }
            adjustment.adjustmentDetails = [adjustmentDetail];
            detailChanged = true;
        }

        if (detailChanged) {
            markAllocationHeaderSelected(selectedAllocationDetails.AllocationUsageHeaderID);
            updateAdjustmentDetailsInAllocationOrder(adjustment.adjustmentDetails);
            setState(prevState => ({
                ...prevState,
                useIpTemplateForCheckboxSelection: true
            }));
        }
    }


    const getPoolsAndSegmentsLocal = (value: string, fieldName: keyof IAdjustmentState) => {
        let sourcePoolsList: IDistributionPool[] = [];
        let sourcePools: { code: string, description: string }[] = []
        let adjustmentPoolsList: IDistributionPool[]
        let adjustmentPools: { code: string, description: string }[] = []
        switch (fieldName) {
            case 'sourceDistribution':
                let sourceDistribution = distributions.find(d => d.distributionCode === value);
                sourcePoolsList = sourceDistribution?.distributionPools ? sourceDistribution.distributionPools : [];
                sourcePools = [...new Set(sourcePoolsList.map(p => p.poolCode))].sort().map(poolCode => ({ code: poolCode, description: poolCode }));
                setState((prevState => ({ ...prevState, sourcePools })));
                break;
            case 'adjustmentDistribution':
                let adjustmentDistribution = distributions.find(d => d.distributionCode === value);
                adjustmentPoolsList = adjustmentDistribution?.distributionPools ? adjustmentDistribution.distributionPools : [];
                adjustmentPools = [...new Set(adjustmentPoolsList.map(p => p.poolCode))].sort().map(poolCode => ({ code: poolCode, description: poolCode }));
                setState((prevState => ({ ...prevState, adjustmentPools })));
                break;
            case 'adjustmentPool': {
                const selectedAdjustmentDistribution = distributions.find(
                    d => d.distributionCode === adjustment.adjustmentDistribution
                );

                adjustmentPoolsList = selectedAdjustmentDistribution?.distributionPools ?? [];

                if (!adjustmentPoolsList.length) {
                    setState(prevState => ({ ...prevState, adjustmentSegments: [] }));
                    break;
                }

                if (adjustmentPoolsList.find(p => p.poolCode === value)) {
                    const adjustmentSegments = buildSegmentOptionsFromPools(adjustmentPoolsList, value);
                    setState(prevState => ({ ...prevState, adjustmentSegments }));
                }

                break;
            }
        }
    }

    const sourceSegments = React.useMemo(
        () =>
            buildSourceSegments(
                distributions,
                adjustment?.sourceDistribution,
                adjustment?.sourcePool ?? []
            ),
        [
            distributions,
            adjustment?.sourceDistribution,
            adjustment?.sourcePool
        ]
    );

    const updateAdjustmentFieldAndChangeState = (value: any, fieldName: keyof IAdjustmentState) => {
        if ((fieldName === 'adjustmentPool' || fieldName === 'adjustmentSegment') && adjustment.adjustmentDetails?.length) {
            const updatedDetails = applyAdjustmentHeaderPoolAndSegmentToAllDetails(
                adjustment.adjustmentDetails,
                fieldName === 'adjustmentPool' ? value : adjustment.adjustmentPool,
                fieldName === 'adjustmentSegment' ? value : adjustment.adjustmentSegment
            );
            updateAdjustmentField(updatedDetails, 'adjustmentDetails');
        }

        updateAdjustmentField(value, fieldName);
        getPoolsAndSegmentsLocal(value, fieldName);
    }

    const onClickSearch = () => {
        setState((prevState => ({ ...prevState, searching: true })))
        const model = buildAllocationSearchModel({
            ipNumbersToSearch: state.ipNumbersToSearch,
            workNumbersToSearch: state.workNumbersToSearch,
            productNumbersToSearch: state.productNumbersToSearch,
            usageGroupTitleToSearch: state.usageGroupTitleToSearch,
            distribution: adjustment?.sourceDistribution,
            pools: adjustment.sourcePool ?? [],
            segment: adjustment.sourceSegment,
            pageSize: DEFAULT_ALLOCATION_SEARCH_PAGE_SIZE,
            continuationToken: null,
        });

        searchAllocation(model).catch(() => {
            setState(prevState => ({ ...prevState, searching: false }));
        });
    }

    const loadMoreAllocations = (): Promise<void> => {
        if (allocationSearchContinuationToken === undefined || allocationSearchContinuationToken === null || !allocationSearchHasMore) {
            return Promise.resolve();
        }

        const model = buildAllocationSearchModel({
            ipNumbersToSearch: state.ipNumbersToSearch,
            workNumbersToSearch: state.workNumbersToSearch,
            productNumbersToSearch: state.productNumbersToSearch,
            distribution: adjustment?.sourceDistribution,
            usageGroupTitleToSearch: state.usageGroupTitleToSearch,
            pools: adjustment.sourcePool ?? [],
            segment: adjustment.sourceSegment,
            pageSize: allocationSearchPageSize ?? DEFAULT_ALLOCATION_SEARCH_PAGE_SIZE,
            continuationToken: allocationSearchContinuationToken,
        });

        return searchAllocation(model);
    };

    const updateWorkNumbersToSearch = (value: string | number | boolean) => {
        const text = String(value);
        setState(prevState => ({
            ...prevState,
            workNumbersToSearch: text ? [text] : [],
        }));
    }

    const updateIPNumbersToSearch = (value: string | number | boolean) => {
        const text = String(value);
        setState(prevState => ({
            ...prevState,
            ipNumbersToSearch: text ? [text] : [],
        }));
    }

    const updateProductNumbersToSearch = (value) => {
        setState((prevState => ({ ...prevState, productNumbersToSearch: value.split(",") })));
    }

    const updateUsageGroupTitleToSearch = (value) => {
        setState((prevState => ({ ...prevState, usageGroupTitleToSearch: value })));
    }

    const completeWorkflowOnSave = (value: boolean) => {
        setState((prevState => ({ ...prevState, isCompleteWorkflowOnSave: value })));
    }

    const renderAllocationSearchFields = () => {
        return (
            <div>
                <div className="row">
                    <div className="col-md-12">
                        <span className="title">{viewData[ADJUSTMENT_SELECT_ALLOCATIONS]}</span>
                    </div>
                </div>
                <div className="adjustment-allocation-search-fields">
                    <div key='searchFields' className="row">
                        <SizedTextDataInput
                            fieldType={"text"}
                            label={viewData[ADJUSTMENT_SEARCH_WORK_NUMBERS]}
                            fieldName={ADJUSTMENT_SEARCH_WORK_NUMBERS}
                            changeData={updateWorkNumbersToSearch}
                            value={state.workNumbersToSearch?.[0] ?? ""}
                            readOnly={isReadonly}
                            isHidden={!viewData[ADJUSTMENT_SEARCH_WORK_NUMBERS]}
                        />
                        <SizedTextDataInput
                            fieldType={"text"}
                            label={viewData[ADJUSTMENT_SEARCH_IP_NUMBERS]}
                            fieldName={ADJUSTMENT_SEARCH_IP_NUMBERS}
                            changeData={updateIPNumbersToSearch}
                            value={state.ipNumbersToSearch?.[0] ?? ""}
                            readOnly={isReadonly}
                            isHidden={!viewData[ADJUSTMENT_SEARCH_IP_NUMBERS]}
                        />
                        <SizedTextDataInput
                            fieldType={"text"}
                            label={viewData[ADJUSTMENT_SEARCH_PRODUCT_NUMBERS]}
                            fieldName={ADJUSTMENT_SEARCH_PRODUCT_NUMBERS}
                            changeData={updateProductNumbersToSearch}
                            value={state.productNumbersToSearch.toString()}
                            readOnly={isReadonly}
                            isHidden={!viewData[ADJUSTMENT_SEARCH_PRODUCT_NUMBERS]}
                        />
                        <SizedTextDataInput
                            fieldType={"text"}
                            label={viewData[ADJUSTMENT_SEARCH_USAGE_GROUP_TITLE]}
                            fieldName={ADJUSTMENT_SEARCH_USAGE_GROUP_TITLE}
                            changeData={updateUsageGroupTitleToSearch}
                            value={state.usageGroupTitleToSearch.toString()}
                            readOnly={isReadonly}
                            isHidden={!viewData[ADJUSTMENT_SEARCH_USAGE_GROUP_TITLE]}
                        />
                    </div>
                    <div className="row">
                        <div className="form-group col-lg-4 col-lg-push-8 col-md-4 col-md-push-8 col-sm-6 col-sm-push-6 col-xs-12 col-xs-push-0">
                            <ActionButton buttonText="Search" disabled={adjustment?.sourceDistribution === '' || adjustment.adjustmentDistribution === '' || isReadonly} buttonAction={onClickSearch} />
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const getConfigureRoleDisabled = () => {
        return !roles?.includes(ALL_ROLES) && !roles?.includes(CONFIGURE_ADJUSTMENTS_ROLE);
    };

    const onSaveAllocationUsageDetailFields = () => {
        const fieldsToSave = editableFields?.default?.filter(
            field => field.componentInstance === ADJUSTMENT_ALLOCATION_USAGE_DETAIL_FIELDS
        );

        if (!fieldsToSave || fieldsToSave.length === 0) {
            return;
        }

        updateComponentFieldsItems(
            fieldsToSave,
            ADJUSTMENT_MAINTENANCE_GRIDS_VIEW,
            ADJUSTMENT_ALLOCATION_USAGE_DETAIL_FIELDS,
            ADJUSTMENT_MAINTENANCE_GRIDS_VIEW,
            tabs,
            activeTab
        );
    };

    const showAllocationUsageDetailFieldsModal = () => {
        setOpenAllocationUsageDetailFieldsModal(true);
        setEditableFields(
            ADJUSTMENT_MAINTENANCE_GRIDS_VIEW,
            ADJUSTMENT_ALLOCATION_USAGE_DETAIL_FIELDS
        );
    };

    useEffect(() => {
        if (!openAllocationUsageDetailFieldsModal) {
            return;
        }

        const tableContents = editableFields?.default ?? [];
        const hasDetailFields = tableContents.some(
            field => field.componentInstance === ADJUSTMENT_ALLOCATION_USAGE_DETAIL_FIELDS
        );

        if (!hasDetailFields) {
            return;
        }

        const fieldsViewData: IRepertoireComponentDataItem = {
            fields: editableFieldsDataView?.fields?.filter(
                x => x.componentInstance === ADJUSTMENT_ALLOCATION_USAGE_DETAIL_FIELDS
            ) ?? []
        };

        if (fieldsViewData.fields.length === 0) {
            setOpenAllocationUsageDetailFieldsModal(false);
            return;
        }

        const title = fieldsViewData.fields.find(item => item.name === TITLE_EDITABLE_FIELDS_VIEW)?.data
            ?? "Adjustment allocation usage details - Data fields configuration";

        const fieldSetting: IShowFieldSettingViewModalProps = {
            componentInstance: EDITABLE_FIELDS,
            componentViewData: fieldsViewData,
            dataGridViewData: dataGridTableData,
            onClickSave: onSaveAllocationUsageDetailFields
        };

        setOpenAllocationUsageDetailFieldsModal(false);
        showUpdateFieldsModal(title, fieldSetting);
    }, [
        openAllocationUsageDetailFieldsModal,
        editableFields,
        editableFieldsDataView,
        dataGridTableData
    ]);

    const renderAllocationSearchResultsGrid = () => {
        if (state.searching) {
            return (<div className="loaderWrapper">
                <div className="spinner-dualball">
                    <div></div>
                    <div></div>
                    <div></div>
                </div>
            </div>)
        } else {
            if (state.updatedAllocationRecord && allocationRecords) {
                return (<>
                    {(state.updatedAllocationRecord && state.updatedAllocationRecord.length > 0) ?
                        <>
                            <div className="row">
                                <div className="col-md-12 adjustment-allocation-usage-detail-fields-action">
                                    <button
                                        data-test-id="ConfigureAdjustmentAllocationUsageDetailFields"
                                        className="editBtn"
                                        title="Configure data fields"
                                        onClick={showAllocationUsageDetailFieldsModal}
                                        disabled={getConfigureRoleDisabled()}
                                    >
                                        <Settings20Regular />
                                    </button>
                                </div>
                            </div>
                            <AllocationSelectionView
                                searchResults={state.updatedAllocationRecord}
                                viewData={viewData}
                                componentInstance={ADJUSTMENT_ALLOCATION_USAGE_DETAIL_FIELDS}
                                addIPAdjustmentDetails={addIPAdjustmentDetails}
                                selectAllAllocationHeaders={selectAllAllocationHeaders}
                                selectAllocationHeader={selectAllocationHeader}
                                customer={customer}
                                adjustment={adjustment}
                                isReadonly={isReadonly}
                                hasMore={allocationSearchHasMore}
                                serverPageSize={allocationSearchPageSize}
                                loadMore={loadMoreAllocations}
                                lookupValues={lookupValues}
                            />
                        </> : <>
                            <div className="flexRow" style={{ justifyContent: 'space-around' }}>
                                No results found.
                            </div>
                        </>}
                </>
                )
            }
        }
    }

        const title = adjustment ? adjustment.adjustmentDistribution : null;

        const changesMade = tabs[activeTab].isReadonly ? false : tabs[activeTab].changesMade;

        if (adjustment) {
            const sourceDistribution = distributions.find(distribution => distribution.distributionCode?.trim().toUpperCase() === adjustment?.sourceDistribution?.trim().toUpperCase());
            const normalizedRightType = sourceDistribution?.distributionRightType?.trim().toUpperCase();
            const sourceDistributionRightType: SocietyAffiliationRightType | undefined = (normalizedRightType === 'PER' || normalizedRightType === 'MEC') ? normalizedRightType : normalizedRightType == null ? 'PER' : undefined;

            return (
                <div>
                    <div><span className="title">{title} ({adjustment.dataSource})</span></div>
                    <If condition={tabs[activeTab].versionNumber != null}>
                        <div><span>Version history number: {tabs[activeTab].versionNumber}</span></div>
                    </If>
                    <button type="button" className="toolbar-toggle" data-toggle="collapse" data-target="#toolbarContainer">
                        <MoreVertical16Regular className="icon" aria-hidden="true" />
                    </button>
                    <div id="toolbarContainer" className="WorkMaintenanceToolbar collapse" ref={toolbarParentRef}>
                        <AdjustmentMaintenanceToolbar
                            changesMade={changesMade}
                            toolbarWidth={state.toolbarWidth}
                            showYesNoPrompt={showYesNoPrompt}
                            showModal={showModal}
                            hideModal={hideModal}
                            scroll={state.scroll}
                            saveResultData={adjustmentMaintenanceSaveResultData}
                            toolbarData={adjustmentMaintenanceToolbarData}
                            saveChanges={saveAdjustment}
                            clearModalSearchResults={clearModalSearchResults}
                            resetMessageBanner={resetMessageBanner}
                            addNewAdjustment={() => addNewAdjustment(lookupValues, true, adjustmentMaintenanceGeneralDataViewData)}
                            activeTab={activeTab}
                            tabs={tabs}
                            adjustment={adjustment}
                            undoAdjustmentChanges={undoChanges}
                            isReadonly={isReadonly}
                            copyAdjustment={copyAdjustment}
                            deleteAdjustment={deleteAdjustment}
                            distributions={distributions}
                            lookupValues={lookupValues}
                            searchVersionHistory={searchAdjustmentVersionHistory}
                            roles={roles}
                            dataSource={dataSource}
                            workflowFieldsData={workflowFieldsData}
                            isWorkflowSessionStarted={isWorkflowSessionStarted}
                            cancelWorkflowSession={cancelWorkflowSession}
                            nextWorkflowItem={nextWorkflowItem}
                            saveWorkflow={saveWorkflow}
                            isOpenForWorkflowSession={isOpenForWorkflowSession}
                            skipWorkflowItem={skipWorkflowItem}
                            workflowSessionItems={workflowSessionItems}
                            currentWorkflowIndex={currentWorkflowIndex}
                            dataActionList={dataActionList}
                            completeWorkflowOnSave={completeWorkflowOnSave}
                        />
                    </div>

                    <AdjustmentMaintenanceGeneralDataView
                        adjustmentMaintenanceGeneralDataViewData={adjustmentMaintenanceGeneralDataViewData}
                        updateAdjustmentField={updateAdjustmentFieldAndChangeState}
                        activeTab={activeTab}
                        tabs={tabs}
                        adjustment={adjustment}
                        isReadonly={isReadonly}
                        lookupValues={lookupValues}
                        sourceDistributions={state.sourceDistributions}
                        sourcePools={state.sourcePools}
                        sourceSegments={sourceSegments}
                        adjustmentDistributions={state.adjustmentDistributions}
                        adjustmentPools={state.adjustmentPools}
                        adjustmentSegments={state.adjustmentSegments}
                        changesMade={changesMade}
                        saveInProgress={saveInProgress}
                        adjustmentBatchOperationInProgress={adjustmentBatchOperationInProgress}
                        postAdjustmentBatch={postAdjustments}
                    />
                    {renderAllocationSearchFields()}
                    {renderAllocationSearchResultsGrid()}
                    <br></br>
                    <AllocationAdjustmentDetailsView
                        key={adjustment.id}
                        allocationAdjustmentDetailsViewData={allocationAdjustmentDetailsViewData}
                        updateAdjustmentField={updateAdjustmentField}
                        showModal={showModal}
                        adjustment={adjustment}
                        allocationRecords={state.updatedAllocationRecord}
                        resetPagination={resetPagination}
                        updatePagination={updatePagination}
                        sortSearchResults={sortSearchResults}
                        searchWorks={undefined}
                        worksSearchResults={worksSearchResults}
                        expandedWorkResults={expandedWorkResults}
                        searchSuccessful={searchSuccessful}
                        expandAll={expandAll}
                        indexOfFirstResult={indexOfFirstResult}
                        indexOfLastResult={indexOfLastResult}
                        resultsPerPage={resultsPerPage}
                        expandWorkResult={undefined}
                        expandAllResults={undefined}
                        currentPage={currentPage}
                        username={undefined}
                        searchMatchWorks={searchMatchWorks}
                        searchMatchProducts={undefined}
                        matchWorkSearchResult={matchWorkSearchResult}
                        expandedMatchWorkResults={expandedMatchWorkResults}
                        distributionTypes={distributionTypes}
                        sourceDistributionRightType={sourceDistributionRightType}
                        lookupSources={lookupSources}
                        usageMatchingDefaultsWorks={usageMatchingDefaultsWorks}
                        addMatchworkToUsage={addMatchworkToUsage}
                        usageExpandAllResults={usageExpandAllResults}
                        expandMatchWorkResult={expandMatchWorkResult}
                        paneIndex={paneIndex}
                        formatFields={undefined}
                        updateUsagesAdvanced={undefined}
                        lookupValues={lookupValues}
                        searchResultsTableData={searchResultsTableData}
                        searchViewData={searchViewData}
                        getWorkDetails={getWorkDetails}
                        isAllocationSelected={state.updatedAllocationRecord.some(item => item.IsSelected === true)}
                        isWorkAdjust={state.isAdjustWork}
                        updateAllocationRecordField={updateAllocationRecordField}
                        saveSuccessful={saveSuccessful}
                        enabledAdjustmentICEWorkSearch={enabledAdjustmentICEWorkSearch}
                        activeTab={activeTab}
                        tabs={tabs}
                        isReadonly={isReadonly} />
                </div>

            );
        }
        return <div>Loading...</div>;
    }

export default AdjustmentMaintenancePage;